import { useState } from 'react'

import { RARITY_COLORS } from '../lib/format.js'

/**
 * Изображение предмета — настоящий рендер скина со Steam CDN. Ссылка приходит
 * с бэкенда вместе с предметом; у себя ничего не хранится, отдаёт Valve.
 *
 * Если ссылки нет или CDN не ответил, на её место встаёт пустая подложка,
 * подкрашенная цветом редкости. Рисовать вместо скина силуэт оружия нельзя:
 * он выглядит как настоящий предмет и врёт про содержимое кейса.
 */

/**
 * CDN умеет отдавать уменьшенные копии по суффиксу в пути. 256px весит вдвое
 * меньше оригинала, а крупнее карточки предмета всё равно не показывают.
 */
const SIZES = {
  sm: '/128fx128f',
  md: '/256fx256f',
  lg: '/360fx360f',
  xl: '/512fx384f',
}

function buildImageUrl(image, size) {
  if (!image) return null
  // Суффикс /256fx256f работает только для эндпоинтов Steam economy.
  // Прямые статические файлы (.png, .webp, cdn.steamstatic.com) при добавлении суффикса отдают 404.
  const canResize =
    image.includes('economy/image') &&
    !/\.(png|jpe?g|webp|svg)(\?.*)?$/i.test(image) &&
    !/\/\d+fx\d+f$/i.test(image)
  return canResize ? `${image}${SIZES[size] ?? SIZES.md}` : image
}

export default function WeaponArt({
  image = null,
  rarity = 'milspec',
  size = 'md',
  className = '',
  title,
}) {
  // Храним именно сломавшийся URL, а не флаг: в рулетке один и тот же
  // компонент переиспользуется под разные предметы, и флаг остался бы
  // висеть на следующем скине, у которого с картинкой всё в порядке.
  const [failedSrc, setFailedSrc] = useState(null)

  const src = buildImageUrl(image, size)

  if (src && src !== failedSrc) {
    return (
      <img
        className={`weapon-art ${className}`}
        src={src}
        alt={title ?? 'Изображение предмета'}
        loading="lazy"
        decoding="async"
        draggable="false"
        onError={() => setFailedSrc(src)}
      />
    )
  }

  return (
    <span
      className={`weapon-art weapon-art-empty ${className}`}
      style={{ '--art-tint': RARITY_COLORS[rarity] ?? RARITY_COLORS.milspec }}
      role="img"
      aria-label={title ?? 'Изображение недоступно'}
    />
  )
}
